import React, { useState, useCallback } from "react"
import { useMutation } from "@apollo/client/react"
import { useNavigate } from "react-router-dom"
import { useNodesState, useEdgesState, ReactFlowProvider } from "@xyflow/react"
import { v4 as uuidv4 } from "uuid"
import { useAppDispatch } from "lib/store"
import { showEditSidebar } from "lib/store/services/editSidebar/slice"
import alertActions from "lib/store/services/Alert/AlertSlice"
import { WorkflowNode, WorkflowEdge, WorkflowNodeData } from "lib/types/workflow"
import { getInitialNodes, flowToApiJson } from "helpers/workflowConverter"
import { extractWorkflowErrorMessage } from "helpers/workflowErrors"

import {
  CreateWorkflowDocument,
  CreateWorkflowMutation,
  CreateWorkflowMutationVariables,
  OrderWorkflowTriggerType
} from "lib/types/generated/graphql"

import PrimaryButton from "components/Ui/Buttons/PrimaryButton"
import SecondaryButton from "components/Ui/Buttons/SecondaryButton"
import WorkflowCanvas from "components/Workflow/Canvas/WorkflowCanvas"
import NodeEditorSidebar from "components/Workflow/NodeEditor/NodeEditorSidebar"

import { ReactComponent as ArrowLeft } from "images/icons/arrow-left.svg"

import {
  Container,
  Header,
  BackLink,
  CanvasSection,
  ButtonGroup,
  NameInput
} from "./CreateWorkflow.styled"

const initialState = getInitialNodes()

export const CreateWorkflow = () => {
  const [workflowName, setWorkflowName] = useState("New Workflow")
  const [selectedNode, setSelectedNode] = useState<WorkflowNode | null>(null)
  const [nodes, setNodes, onNodesChange] = useNodesState<WorkflowNode>(initialState.nodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState<WorkflowEdge>(initialState.edges)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const workflowIdRef = React.useRef<string>("")

  const [createWorkflow, { loading: saving }] = useMutation<
    CreateWorkflowMutation,
    CreateWorkflowMutationVariables
  >(CreateWorkflowDocument, {
    onCompleted: () => {
      dispatch(
        alertActions.actions.createAlert({
          message: "Workflow created successfully",
          type: "success"
        })
      )
      navigate(`/settings/workflows/${workflowIdRef.current}`)
    },
    onError: (error) => {
      dispatch(
        alertActions.actions.createAlert({
          message: extractWorkflowErrorMessage(error) ?? "Failed to create workflow",
          type: "error"
        })
      )
    }
  })

  const handleNodeClick = useCallback(
    (_event: React.MouseEvent, node: WorkflowNode) => {
      setSelectedNode(node)
      dispatch(showEditSidebar())
    },
    [dispatch]
  )

  const handleNodeSave = useCallback(
    (nodeId: string, data: WorkflowNodeData) => {
      setNodes((nds) =>
        nds.map((n) => (n.id === nodeId ? { ...n, data } : n))
      )
      setSelectedNode(null)
    },
    [setNodes]
  )

  const handleNodeDelete = useCallback(
    (nodeId: string) => {
      setNodes((nds) => nds.filter((n) => n.id !== nodeId))
      setEdges((eds) =>
        eds.filter((e) => e.source !== nodeId && e.target !== nodeId)
      )
      setSelectedNode(null)
    },
    [setNodes, setEdges]
  )

  const handleSave = () => {
    const apiNodes = flowToApiJson(nodes, edges)
    const workflowId = uuidv4()
    workflowIdRef.current = workflowId

    createWorkflow({
      variables: {
        workflowId,
        input: {
          name: workflowName,
          trigger: OrderWorkflowTriggerType.OrderCreated,
          then: JSON.stringify(apiNodes)
        }
      }
    })
  }

  return (
    <Container>
      <Header>
        <BackLink onClick={() => navigate("/settings/workflows/")}>
          <ArrowLeft /> Back
        </BackLink>
        <NameInput
          aria-label="Workflow name"
          value={workflowName}
          onChange={(e) => setWorkflowName(e.target.value)}
          placeholder="Workflow name"
          maxLength={100}
        />
        <ButtonGroup>
          <SecondaryButton handleClick={() => navigate("/settings/workflows/")}>
            Cancel
          </SecondaryButton>
          <PrimaryButton handleClick={handleSave} loading={saving}>
            Save Workflow
          </PrimaryButton>
        </ButtonGroup>
      </Header>
      <ReactFlowProvider>
        <CanvasSection>
          <WorkflowCanvas
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            setNodes={setNodes}
            setEdges={setEdges}
            onNodeClick={handleNodeClick}
          />
        </CanvasSection>
      </ReactFlowProvider>
      <NodeEditorSidebar
        selectedNode={selectedNode}
        onSave={handleNodeSave}
        onDelete={handleNodeDelete}
      />
    </Container>
  )
}
